import type { Staff } from "@payezy/api-client/admin";
import { listStaffOptions, provisionStaffMutation, setStaffRolesMutation, setStaffStatusMutation } from "@payezy/api-client/admin/query";
import { Badge, Button, Card, Dialog, Field, formatDate, Input, PageHeader, Table, TD, TH, THead, TR, toast } from "@payezy/ui";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { UserPlus } from "lucide-react";
import { useState } from "react";
import { showError } from "../lib/notify";
import { useStaff } from "../lib/staff";

const roleHelp: Record<string, string> = {
  superadmin: "Everything, including staff management and write view-as.",
  ops: "Customers, payments, recon, partners, view-as (read and write).",
  compliance: "KYC/KYB review, screening, restrictions, audit log.",
  finance: "Payments, reconciliation, partner health.",
  support: "Customer lookup, read-only view-as, requests for others to approve.",
};

export function StaffPage() {
  const me = useStaff();
  const data = useSuspenseQuery(listStaffOptions()).data;
  const [editing, setEditing] = useState<Staff | "new" | null>(null);
  const qc = useQueryClient();
  const status = useMutation({
    ...setStaffStatusMutation(),
    onSuccess: () => void qc.invalidateQueries({ queryKey: listStaffOptions().queryKey }),
    onError: showError,
  });
  return (
    <>
      <PageHeader
        title="Staff"
        description="Staff sign in to a separate identity realm with mandatory MFA. New staff get a one-time setup email; changes here require a recent sign-in."
        actions={<Button onClick={() => setEditing("new")}><UserPlus className="size-4" /> Add staff</Button>}
      />
      <Card>
        <Table>
          <THead><TR><TH>Name</TH><TH>Email</TH><TH>Roles</TH><TH>Status</TH><TH>Last seen</TH><TH /></TR></THead>
          <tbody>
            {data.items.map((s) => (
              <TR key={s.id}>
                <TD className="font-medium">{s.name}</TD>
                <TD>{s.email}</TD>
                <TD>{s.roles.map((r) => <Badge key={r} className="mr-1">{r}</Badge>)}</TD>
                <TD><Badge tone={s.status === "active" ? "success" : "neutral"}>{s.status}</Badge></TD>
                <TD>{formatDate(s.last_seen_at, true)}</TD>
                <TD className="whitespace-nowrap text-right">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(s)}>Roles</Button>
                  {s.id !== me.id && (
                    <Button
                      size="sm"
                      variant={s.status === "active" ? "ghost" : "secondary"}
                      onClick={() => status.mutate({ path: { staffId: s.id }, body: { status: s.status === "active" ? "disabled" : "active" } })}
                    >
                      {s.status === "active" ? "Disable" : "Enable"}
                    </Button>
                  )}
                </TD>
              </TR>
            ))}
          </tbody>
        </Table>
      </Card>
      {editing && <StaffDialog staff={editing === "new" ? null : editing} roles={data.roles} onClose={() => setEditing(null)} />}
    </>
  );
}

function StaffDialog({ staff, roles, onClose }: { staff: Staff | null; roles: string[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [selected, setSelected] = useState<string[]>(staff?.roles ?? ["support"]);
  const done = (msg: string) => {
    toast.success(msg);
    onClose();
    void qc.invalidateQueries({ queryKey: listStaffOptions().queryKey });
  };
  const provision = useMutation({ ...provisionStaffMutation(), onSuccess: () => done("Invitation sent."), onError: showError });
  const update = useMutation({ ...setStaffRolesMutation(), onSuccess: () => done("Roles updated."), onError: showError });
  const toggle = (r: string) => setSelected((s) => (s.includes(r) ? s.filter((x) => x !== r) : [...s, r]));
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={staff ? `Roles for ${staff.name}` : "Add a staff member"}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            loading={provision.isPending || update.isPending}
            disabled={selected.length === 0 || (!staff && (!email.includes("@") || !name.trim()))}
            onClick={() => (staff ? update.mutate({ path: { staffId: staff.id }, body: { roles: selected } }) : provision.mutate({ body: { email, name, roles: selected } }))}
          >
            {staff ? "Save" : "Send invitation"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {!staff && (
          <>
            <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
            <Field label="Work email"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
          </>
        )}
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-medium">Roles</legend>
          {roles.map((r) => (
            <label key={r} className="flex cursor-pointer items-start gap-3 rounded-lg p-2 ring-1 ring-ink-200 hover:bg-ink-50">
              <input type="checkbox" className="mt-1" checked={selected.includes(r)} onChange={() => toggle(r)} />
              <span><span className="text-sm font-medium">{r}</span><span className="block text-xs text-ink-500">{roleHelp[r]}</span></span>
            </label>
          ))}
        </fieldset>
      </div>
    </Dialog>
  );
}
